import React, { useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import { FaClock, FaBolt } from 'react-icons/fa';
import NavBar from './NavBar.js';
import Footer from './Footer.js';
import '../styles/Contact.css';
import { useRecaptchaV2 } from './useRecaptchaV2.js';

const contactInfo = {
  hero: {
    eyebrow: 'Kontakta oss',
    title: 'Låt oss prata om dina siffror',
    lead: 'Boka ett kort intro, få en snabb bedömning och en plan för hur vi tar hand om din redovisning.',
    highlights: [
      { icon: <FaClock />, label: 'Svarstid', value: 'Inom 2 timmar helgfria vardagar' },
      { icon: <FaBolt />, label: 'Onboarding', value: 'Klar start inom 5 arbetsdagar' },
    ],
  },
};

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const MIN_SUBMIT_DELAY_MS = 3000;
const SUBMIT_COOLDOWN_MS = 30000;
const CONTACT_LAST_SENT_KEY = 'contactLastSentAt';

const initialForm = {
  name: '',
  email: '',
  phone: '',
  orgnr: '',
  topic: '',
  message: '',
  website: '',
};

function Contact() {
  const [formData, setFormData] = useState(initialForm);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSending, setIsSending] = useState(false);
  const formStartedAtRef = useRef(Date.now());
  const { containerRef: recaptchaContainerRef, getToken, reset: resetRecaptcha } = useRecaptchaV2(RECAPTCHA_SITE_KEY);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });
    setIsSending(true);

    if (formData.website.trim()) {
      setStatus({ type: 'error', message: 'Kunde inte skicka just nu. Försök igen senare.' });
      setIsSending(false);
      return;
    }

    if (Date.now() - formStartedAtRef.current < MIN_SUBMIT_DELAY_MS) {
      setStatus({ type: 'error', message: 'Vänta några sekunder och försök igen.' });
      setIsSending(false);
      return;
    }

    try {
      const lastSent = Number(sessionStorage.getItem(CONTACT_LAST_SENT_KEY) || '0');
      if (lastSent && Date.now() - lastSent < SUBMIT_COOLDOWN_MS) {
        setStatus({ type: 'error', message: 'Vänta en stund innan du skickar igen.' });
        setIsSending(false);
        return;
      }
    } catch (_) {
      // ignore storage errors
    }

    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      setStatus({ type: 'error', message: 'E-postinställningar saknas. Kontakta admin.' });
      setIsSending(false);
      return;
    }

    if (!RECAPTCHA_SITE_KEY) {
      setStatus({ type: 'error', message: 'reCAPTCHA saknas. Kontakta admin.' });
      setIsSending(false);
      return;
    }

    const recaptchaToken = getToken();
    if (!recaptchaToken) {
      setStatus({ type: 'error', message: 'Bekräfta reCAPTCHA innan du skickar.' });
      setIsSending(false);
      return;
    }

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone,
          orgnr: formData.orgnr,
          topic: formData.topic,
          message: formData.message,
          'g-recaptcha-response': recaptchaToken,
        },
        {
          publicKey: EMAILJS_PUBLIC_KEY,
        },
      );

      setStatus({ type: 'success', message: 'Tack! Vi har fått ditt meddelande och hör av oss inom en arbetsdag.' });
      setFormData(initialForm);
      formStartedAtRef.current = Date.now();
      resetRecaptcha();
      try {
        sessionStorage.setItem(CONTACT_LAST_SENT_KEY, String(Date.now()));
      } catch (_) {
        // ignore storage errors
      }
    } catch (err) {
      setStatus({ type: 'error', message: 'Kunde inte skicka just nu. Försök igen eller mejla oss direkt.' });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="contact-page">
      <NavBar />

      <main className="contact-shell">
        <section className="contact-hero">
          <div className="hero-text">
            <p className="eyebrow">{contactInfo.hero.eyebrow}</p>
            <h1>{contactInfo.hero.title}</h1>
            <p className="lead">{contactInfo.hero.lead}</p>
            <div className="hero-highlights">
              {contactInfo.hero.highlights.map((item) => (
                <div className="highlight" key={item.label}>
                  <span className="ico">{item.icon}</span>
                  <div>
                    <p className="muted small">{item.label}</p>
                    <strong>{item.value}</strong>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="contact-form">
          <div className="form-copy">
            <p className="eyebrow">Skicka ett meddelande</p>
            <h2>Berätta kort vad du behöver hjälp med</h2>
            <p className="muted">Vi återkommer inom en arbetsdag. Lämna gärna ett telefonnummer om du vill bli uppringd.</p>
            <div className="form-pills">
              <span className="pill">Trygg onboarding</span>
              <span className="pill">Fast kontaktperson</span>
              <span className="pill">Snabb återkoppling</span>
            </div>
          </div>
          <form className="form-grid" onSubmit={handleSubmit}>
            <div className="form-stack">
              <label>
                Namn
                <input
                  type="text"
                  name="name"
                  placeholder="Ditt namn"
                  value={formData.name}
                  onChange={handleChange}
                  maxLength={120}
                  required
                />
              </label>
              <label>
                E-post
                <input
                  type="email"
                  name="email"
                  placeholder="din@mejl.se"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </label>
              <label>
                Telefon
                <input
                  type="tel"
                  name="phone"
                  placeholder="070-123 45 67"
                  value={formData.phone}
                  onChange={handleChange}
                  maxLength={40}
                />
              </label>
              <label>
                Organisationsnummer (valfritt)
                <input
                  type="text"
                  name="orgnr"
                  placeholder="556012-3456"
                  value={formData.orgnr}
                  onChange={handleChange}
                  maxLength={30}
                />
              </label>
            </div>
            <div className="form-stack">
              <label>
                Vad gäller det?
                <select name="topic" value={formData.topic} onChange={handleChange} required>
                  <option value="" disabled>Välj område</option>
                  <option value="bokföring">Bokföring & redovisning</option>
                  <option value="deklaration">Deklaration & skatt</option>
                  <option value="rådgivning">Ekonomisk rådgivning</option>
                  <option value="övrigt">Övrigt</option>
                </select>
              </label>
              <label>
                Meddelande
                <textarea
                  name="message"
                  rows="7"
                  placeholder="Beskriv kort ditt behov"
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={2000}
                  required
                />
              </label>
              <label className="sr-only" aria-hidden="true">
                Lämna detta fält tomt
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </label>
            </div>
            <div className="form-actions span-2">
              {RECAPTCHA_SITE_KEY ? (
                <div ref={recaptchaContainerRef} />
              ) : (
                <p className="muted small">reCAPTCHA är inte konfigurerat.</p>
              )}
              <button type="submit" className="cta-btn" disabled={isSending}>
                {isSending ? 'Skickar...' : 'Skicka'}
              </button>
              {status.message && (
                <p className={`status ${status.type}`}>{status.message}</p>
              )}
              <p className="muted small">Genom att skicka meddelandet godkänner du vår hantering av kontaktuppgifter.</p>
            </div>
          </form>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default Contact;
